// Reading another package's export into plain rows for the import (#16). The
// database decides what each row does (ledger.import_contacts); this only
// reads the file and says if it isn't the kind of file expected.
import type { ContactImportRow, OpenItemRow } from '../../data/source';

export type ImportPackage = 'sage_accounting' | 'sage50' | 'big_red_cloud' | 'quickbooks' | 'xero' | 'surf';
export type ImportKind = 'customer' | 'supplier' | 'invoices' | 'purchases';

/**
 * The packages books come from (#40: not only Sage). `short` names it in the screens ("Sage nominal code"),
 * `codeWord` is what it calls an account's code, `tb` says whether its trial balance can be read yet and
 * how to export it, and `sageRanges` whether Sage's code ranges apply (income and costs 4000–8999, db/041).
 */
export const PACKAGES: { key: ImportPackage; label: string; short: string; codeWord: string; ready: boolean; how: string;
  tb: boolean; tbHow: string; sageRanges: boolean }[] = [
  { key: 'sage_accounting', label: 'Sage Accounting (cloud)', short: 'Sage', codeWord: 'nominal code', ready: true,
    how: 'In Sage Accounting: Contacts → Customers (or Suppliers) → Export. Keep the file as it comes (CSV).',
    tb: true, tbHow: 'In Sage Accounting, run Reports → Trial Balance for the migration date (usually the last day of a VAT period you’ve filed) and export it as CSV.',
    sageRanges: true },
  { key: 'sage50', label: 'Sage 50 (Line 50)', short: 'Sage', codeWord: 'nominal code', ready: false, how: 'Coming next, once we have sample files.',
    tb: false, tbHow: 'Coming next, once we have sample files.', sageRanges: true },
  { key: 'big_red_cloud', label: 'Big Red Cloud', short: 'Big Red Cloud', codeWord: 'account code', ready: false, how: 'Coming later.',
    tb: false, tbHow: 'Coming later.', sageRanges: false },
  { key: 'quickbooks', label: 'QuickBooks Online', short: 'QuickBooks', codeWord: 'account', ready: false, how: 'Coming later.',
    tb: false, tbHow: 'Coming later.', sageRanges: false },
  { key: 'xero', label: 'Xero', short: 'Xero', codeWord: 'account code', ready: false, how: 'Coming later.',
    tb: false, tbHow: 'Coming later.', sageRanges: false },
  { key: 'surf', label: 'Surf Accounts', short: 'Surf', codeWord: 'account code', ready: false, how: 'Coming later.',
    tb: false, tbHow: 'Coming later.', sageRanges: false },
];

/** The package a batch came from, by its source format (unknown formats read as themselves). */
export function packageOf(sourceFormat?: string | null) {
  return PACKAGES.find((p) => p.key === sourceFormat)
    ?? { key: sourceFormat as ImportPackage, label: sourceFormat ?? 'the file', short: 'the file', codeWord: 'code', ready: false, how: '', tb: false, tbHow: '', sageRanges: false };
}

export const KINDS: { key: ImportKind; label: string; ready: boolean }[] = [
  { key: 'customer', label: 'Customers', ready: true },
  { key: 'supplier', label: 'Suppliers', ready: true },
  { key: 'invoices', label: 'Unpaid invoices and credit notes', ready: true },
  { key: 'purchases', label: 'Unpaid bills and supplier credits', ready: true },
];

/** CSV as Sage writes it: a BOM, quoted fields with "" for a quote, CRLF or LF lines. */
/** keepEmpty keeps blank lines, so row numbers match the spreadsheet's. */
export function parseCsv(text: string, keepEmpty = false): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  const s = text.replace(/^﻿/, '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (quoted) {
      if (ch === '"' && s[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"' && field === '') quoted = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (keepEmpty || row.some((f) => f !== '')) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (keepEmpty || row.some((f) => f !== '')) rows.push(row);
  return rows;
}

export type ReadResult = { rows: ContactImportRow[]; problem: string | null; warning: string | null };

/** Sage Accounting's Contacts export, customers or suppliers. */
export function readSageAccounting(text: string, role: 'customer' | 'supplier'): ReadResult {
  const [head, ...body] = parseCsv(text);
  if (!head) return { rows: [], problem: 'The file is empty.', warning: null };
  const col = (name: string) => head.findIndex((h) => h.trim().toLowerCase() === name.toLowerCase());
  const name = col('Company Name*') >= 0 ? col('Company Name*') : col('Company Name');
  if (name < 0 || col('Unique ID') < 0) {
    return { rows: [], problem: 'This doesn’t look like a Sage Accounting contacts export: it needs the “Unique ID” and “Company Name*” columns. Export again from Contacts in Sage, and keep the file as CSV.', warning: null };
  }
  const isCustomerFile = col('Statement Run') >= 0;
  const warning = role === 'supplier' && isCustomerFile ? 'This looks like a customer export (it has a Statement Run column). Check you chose the right file.'
    : role === 'customer' && !isCustomerFile ? 'This looks like a supplier export (it has no Statement Run column). Check you chose the right file.' : null;
  const get = (r: string[], h: string) => {
    const i = col(h);
    const v = i >= 0 ? (r[i] ?? '').trim() : '';
    return v === '' ? null : v;
  };
  const rows = body.map((r, i): ContactImportRow => ({
    row: i + 2,
    unique_id: get(r, 'Unique ID'),
    reference: get(r, 'Reference'),
    name: (r[name] ?? '').trim() || null,
    email: get(r, 'Main Contact Email'),
    vat_number: get(r, 'VAT Number'),
    currency: get(r, 'Currency'),
    terms: get(r, 'Payment Terms'),
    active: get(r, 'Active'),
    statements: get(r, 'Statement Run'),
    address: {
      line1: get(r, 'Main Address Line 1'), line2: get(r, 'Main Address Line 2'), town: get(r, 'Main Address Town'),
      county: get(r, 'Main Address County'), postcode: get(r, 'Main Address Post Code'), country: get(r, 'Main Address Country'),
    },
  }));
  if (rows.length === 0) return { rows, problem: 'The file has its headings but no contacts.', warning };
  return { rows, problem: null, warning };
}

// ---- Unpaid invoices, credit notes and bills (go-live item 9) -----------------------------------------
// Sage's aged debtors / creditors export, or any CSV with the same columns. Headings are matched loosely
// (case, spaces and punctuation ignored), first match wins, so a hand-made CSV works too:
//   Type, Number, Account, Name, Date, Due date, Total, Outstanding, Currency
const HEADINGS: Record<keyof Omit<OpenItemRow, 'row'>, string[]> = {
  contact_ref: ['customer reference', 'supplier reference', 'account reference', 'contact reference', 'customer ref', 'supplier ref',
    'account ref', 'a/c', 'ac', 'account', 'account code', 'customer code', 'supplier code'],
  contact_name: ['customer name', 'supplier name', 'contact name', 'company name', 'customer', 'supplier', 'contact', 'name'],
  number: ['invoice number', 'invoice no', 'inv no', 'document number', 'document no', 'transaction reference', 'number', 'reference', 'ref', 'no'],
  type: ['type', 'transaction type', 'document type'],
  date: ['invoice date', 'transaction date', 'document date', 'date'],
  due_date: ['due date', 'due'],
  total: ['invoice total', 'total amount', 'original amount', 'gross', 'total', 'amount'],
  outstanding: ['outstanding amount', 'amount outstanding', 'amount due', 'balance due', 'outstanding', 'unpaid', 'balance', 'due amount'],
  currency: ['currency', 'currency code'],
};
const squash = (h: string) => h.toLowerCase().replace(/[^a-z0-9/]/g, ' ').replace(/\s+/g, ' ').trim();

/** dd/mm/yyyy (Irish and UK exports), yyyy-mm-dd, or dd-mm-yy, as an ISO date; null when it can't be read. */
export function readDate(v: string | undefined): string | null {
  const t = (v ?? '').trim();
  if (!t) return null;
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}`;
  m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (m) {
    const y = m[3]!.length === 2 ? `20${m[3]}` : m[3]!;
    const d = Number(m[1]), mo = Number(m[2]);
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return null;
}

/** "€1,230.00", "(76.00)" or "-76.00" as a plain number string; null when it isn't one. */
export function readAmount(v: string | undefined): string | null {
  let t = (v ?? '').trim().replace(/[€£$,\s]/g, '');
  if (!t) return null;
  let neg = false;
  if (/^\(.*\)$/.test(t)) { neg = true; t = t.slice(1, -1); }
  if (t.endsWith('-')) { neg = true; t = t.slice(0, -1); }
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  return (neg && !t.startsWith('-') ? '-' : '') + t;
}

const CREDIT = /^(sc|pc|cn|credit|credit note|sales credit|sales credit note|purchase credit|purchase credit note|supplier credit|supplier credit note)$/;

export type OpenItemsRead = { rows: OpenItemRow[]; problem: string | null; found: string[] };

/** Sage's aged debtors (creditors) export, or a CSV laid out like it, into rows for ledger.check_open_items. */
export function readOpenItems(text: string): OpenItemsRead {
  const table = parseCsv(text, true);
  while (table.length && table[table.length - 1]!.every((f) => f === '')) table.pop();
  // Some reports put a title or the date above the headings: take the first row that has a date and an amount column.
  let at = -1;
  let cols: Partial<Record<keyof typeof HEADINGS, number>> = {};
  for (let i = 0; i < Math.min(table.length, 10) && at < 0; i++) {
    const heads = table[i]!.map(squash);
    const found: typeof cols = {};
    const used = new Set<number>();
    for (const key of Object.keys(HEADINGS) as (keyof typeof HEADINGS)[]) {
      for (const name of HEADINGS[key]) {
        const idx = heads.findIndex((h, j) => h === name && !used.has(j));
        if (idx >= 0) { found[key] = idx; used.add(idx); break; }
      }
    }
    if (found.date !== undefined && (found.outstanding !== undefined || found.total !== undefined)) { at = i; cols = found; }
  }
  if (at < 0) {
    return { rows: [], found: (table[0] ?? []).filter(Boolean), problem: 'This doesn’t look like an aged debtors or creditors export: it needs columns for the date and the amount unpaid, '
      + 'and a number and the customer or supplier. Export it again from Sage as CSV, or lay a CSV out as: Type, Number, Account, Name, Date, Due date, Total, Outstanding.' };
  }
  const get = (r: string[], k: keyof typeof HEADINGS) => (cols[k] === undefined ? '' : (r[cols[k]!] ?? '').trim());
  const rows: OpenItemRow[] = [];
  table.slice(at + 1).forEach((r, i) => {
    // Sub-totals and totals lines have no number and no date: leave them out.
    if (!get(r, 'number') && !get(r, 'date')) return;
    const outstanding = readAmount(get(r, 'outstanding') || get(r, 'total'));
    const total = readAmount(get(r, 'total'));
    const t = squash(get(r, 'type'));
    const credit = t ? CREDIT.test(t) || t.includes('credit') : Number(outstanding ?? total ?? '0') < 0;
    rows.push({
      row: at + i + 2, type: credit ? 'credit' : 'invoice', number: get(r, 'number') || null,
      contact_ref: get(r, 'contact_ref') || null, contact_name: get(r, 'contact_name') || null,
      date: readDate(get(r, 'date')), due_date: readDate(get(r, 'due_date')),
      total, outstanding, currency: get(r, 'currency').toUpperCase() || null,
    });
  });
  if (rows.length === 0) return { rows, found: table[at]!, problem: 'The file has its headings but no invoices.' };
  return { rows, found: table[at]!, problem: null };
}
